import type { ParcelRow } from '@mollusk/shared';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';
import { LoadingPill } from '../../components/LoadingPill';
import { ParcelDetailModal } from './ParcelDetailModal';
import { wktToGeoJSON } from './wkt';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN ?? '';

const SOURCE_ID = 'parcels';
const FILL_LAYER = 'parcels-fill';
const LINE_LAYER = 'parcels-line';
const TARGET_LINE = 'target-line';

export function ParcelMap({
  center,
  targetParcel,
}: {
  center: { lat: number; lng: number };
  targetParcel: ParcelRow | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null);
  const [active, setActive] = useState<ParcelRow[]>([]);

  // Initialize the map.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [center.lng, center.lat],
      zoom: 17,
    });
    // Scrolling the page shouldn't zoom the map; enable it after a click (#171).
    map.scrollZoom.disable();
    map.on('click', () => map.scrollZoom.enable());
    mapRef.current = map;
    map.on('load', () => {
      map.addSource(SOURCE_ID, { type: 'geojson', data: emptyFC() });
      map.addLayer({
        id: FILL_LAYER,
        type: 'fill',
        source: SOURCE_ID,
        paint: { 'fill-color': '#1f7a8c', 'fill-opacity': 0.3 },
      });
      map.addLayer({
        id: LINE_LAYER,
        type: 'line',
        source: SOURCE_ID,
        paint: { 'line-color': '#022b3a', 'line-width': 1, 'line-opacity': 0.5 },
      });
      map.addLayer({
        id: TARGET_LINE,
        type: 'line',
        source: SOURCE_ID,
        filter: ['==', ['get', 'is_target'], true],
        paint: { 'line-color': '#d62828', 'line-width': 3 },
      });
      const updateBbox = () => {
        const b = map.getBounds();
        if (b) setBbox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      };
      updateBbox();
      map.on('moveend', updateBbox);
      map.on('click', FILL_LAYER, (e) => {
        // The same lat/lng can have multiple parcel rows from different
        // sources (e.g. Austin city + Travis county); Mapbox includes all
        // features at the click point in `e.features`, so collect them all
        // and dedupe by parcel_id rather than picking the first one.
        const rows: ParcelRow[] = [];
        for (const f of e.features ?? []) {
          const raw = f.properties?.__row;
          if (typeof raw !== 'string') continue;
          try {
            rows.push(JSON.parse(raw) as ParcelRow);
          } catch {
            // ignore unparseable row
          }
        }
        const deduped = Array.from(new Map(rows.map((r) => [r.parcel_id, r])).values());
        if (deduped.length) setActive(deduped);
      });
      map.on('mouseenter', FILL_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', FILL_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [center.lat, center.lng]);

  // Fetch parcels in viewport.
  const parcelsQuery = useQuery({
    queryKey: ['parcels', bbox],
    enabled: !!bbox,
    queryFn: () => {
      if (!bbox) throw new Error('no bbox');
      return api.parcels(bbox);
    },
  });

  // Update map source whenever data changes.
  //
  // Race: in React 19 StrictMode the init effect runs twice in dev, so the
  // map's `load` event may fire AFTER the parcels query has already
  // resolved. If we naively `getSource()` here, the source won't exist yet
  // and we'd silently skip applying the data — and there'd be no signal to
  // re-run later when the source is finally added. Guard by listening for
  // `load` if the source isn't ready, so the data lands either way.
  useEffect(() => {
    const map = mapRef.current;
    const data = parcelsQuery.data;
    if (!map || !data) return;

    const targetApn = targetParcel?.parcel_id_normalized;
    const apply = () => {
      const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      const features: GeoJSON.Feature[] = data.parcels.flatMap((p) => {
        const geom = p.geometry ? wktToGeoJSON(p.geometry) : null;
        if (!geom) return [];
        return [
          {
            type: 'Feature',
            geometry: geom,
            properties: {
              is_target: targetApn != null && p.parcel_id_normalized === targetApn,
              __row: JSON.stringify(p),
            },
          },
        ];
      });
      src.setData({ type: 'FeatureCollection', features });
    };

    if (map.getSource(SOURCE_ID)) {
      apply();
      return;
    }
    map.once('load', apply);
    return () => {
      map.off('load', apply);
    };
  }, [parcelsQuery.data, targetParcel?.parcel_id_normalized]);

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Map
      </Typography>
      {parcelsQuery.error instanceof ApiError ? (
        <Typography color="error">{parcelsQuery.error.message}</Typography>
      ) : null}
      <Box sx={{ position: 'relative', width: '100%', height: 480 }}>
        <Box
          ref={containerRef}
          sx={{ position: 'absolute', inset: 0, borderRadius: 1, overflow: 'hidden' }}
        />
        {parcelsQuery.isFetching ? (
          <LoadingPill
            data-testid="parcels-loading"
            label="Loading parcels…"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          />
        ) : null}
      </Box>
      <ParcelDetailModal parcels={active} onClose={() => setActive([])} />
    </Paper>
  );
}

function emptyFC(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}
